/**
 * モードプロファイル (Sprint BG-DataSettings 30、Phase 5)。
 *
 * 旧 v1: 4 モードの初期 dataSettings (userFields / postFields) は
 * `modeTemplates.ts` (251 行 static) にハードコード、新地区作成時に
 * `getModeTemplate(mode)` でコピーしていた。
 *
 * 新方式 (本ファイル): Firestore master 駆動化。
 *   - path: `/modeProfiles/{profileId}` (master 単位の global、地区横断)
 *   - 新地区作成時に profileId を指定すると、その profile の
 *     userFields / postFields / userProfileOptions / recommendedMasters を
 *     コピーして地区初期値とする (profileId 未指定なら `getModeTemplate(mode)`
 *     にフォールバック、後方互換)
 *   - 既存地区への一括反映も可能 (admin UI から差分を選んで上書き)
 *
 * MasterCommon は継承しない: profile は M1〜M9 とは異なる「メタマスター」
 * 扱いで、版管理は profileVersion 文字列で行う (snapshot tag、UUID で OK)。
 * 認可は firestore.rules 側で master role のみ write 可とする。
 *
 * 参考:
 *   - shared-types/src/domain/dataSettings.ts (FieldDef / DataSettings 型)
 *   - shared-types/src/domain/modeTemplates.ts (旧 static template、fallback)
 *   - shared-types/src/domain/analysisDisplayConfig.ts (parseAnalysisDisplayRules パターン)
 */
import { normalizeFieldDefs } from './dataSettings.js';
/* ===== parse helpers ===== */
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function pickString(v) {
    return typeof v === 'string' ? v : undefined;
}
/**
 * mode を解決する。組込 4 種に限らず、管理者が自由に定義した key (例 `'fukushi'`) も許容する
 * (B2、issue #32)。非空文字列なら採用 (前後空白は除去)。空 / 非文字列は undefined。
 */
function pickMode(v) {
    if (typeof v !== 'string')
        return undefined;
    const t = v.trim();
    return t.length > 0 ? t : undefined;
}
/**
 * `FieldDef[]` 候補値を安全に正規化。
 * 不正 entry は drop、空配列は空配列のまま返す。
 *
 * 必須 field (key/label/type/mode) のいずれかが不足する entry は drop。
 * options が string[] の旧 schema は `normalizeFieldDefs` 側で吸収済。
 */
function parseFieldDefs(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const entry of v) {
        if (!isRecord(entry))
            continue;
        const key = pickString(entry.key);
        const label = pickString(entry.label);
        const type = pickString(entry.type);
        const mode = pickString(entry.mode);
        if (!key || !label || !type || !mode)
            continue;
        // 正規化前 raw shape を組み立て、normalizeFieldDefs に options 正規化を委譲。
        // **optional フィールドは値があるときのみキーを付ける** (明示 undefined を入れない)。
        // 旧実装は `optionsMeta: ... : undefined` 等で undefined を埋めており、これが
        // normalizeFieldDef の `{...field}` スプレッドで温存され、ModeProfile スナップショットを
        // District.dataSettings へコピーする際に Firestore setDoc が
        // 「Unsupported field value: undefined」で reject する原因になっていた。
        const raw = {
            key,
            label,
            type,
            mode,
            options: Array.isArray(entry.options) ? entry.options : [],
            builtIn: typeof entry.builtIn === 'boolean' ? entry.builtIn : false,
            description: typeof entry.description === 'string' ? entry.description : '',
            definition: typeof entry.definition === 'string' ? entry.definition : '',
        };
        if (isRecord(entry.optionsMeta))
            raw.optionsMeta = entry.optionsMeta;
        if (typeof entry.visible === 'boolean')
            raw.visible = entry.visible;
        if (typeof entry.displayOrder === 'number' && Number.isFinite(entry.displayOrder)) {
            raw.displayOrder = entry.displayOrder;
        }
        if (typeof entry.useAsAnalysisAxis === 'boolean') {
            raw.useAsAnalysisAxis = entry.useAsAnalysisAxis;
        }
        out.push(raw);
    }
    return normalizeFieldDefs(out);
}
function parseUserProfileOptions(v) {
    if (!isRecord(v))
        return undefined;
    const out = {};
    for (const [k, val] of Object.entries(v)) {
        if (!Array.isArray(val))
            continue;
        const list = val.filter((x) => typeof x === 'string');
        if (list.length > 0)
            out[k] = list;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
function parseRecommendedMasters(v) {
    if (!isRecord(v))
        return undefined;
    const out = {};
    const a = pickString(v.analysisSettingsId);
    if (a)
        out.analysisSettingsId = a;
    const d = pickString(v.dialogueSettingsId);
    if (d)
        out.dialogueSettingsId = d;
    const dc = pickString(v.displayConfigId);
    if (dc)
        out.displayConfigId = dc;
    const of = pickString(v.outputFormatId);
    if (of)
        out.outputFormatId = of;
    if (Array.isArray(v.categoryMasterIds)) {
        const ids = v.categoryMasterIds.filter((x) => typeof x === 'string');
        if (ids.length > 0)
            out.categoryMasterIds = ids;
    }
    const sd = pickString(v.scoreDimensionId);
    if (sd)
        out.scoreDimensionId = sd;
    return Object.keys(out).length > 0 ? out : undefined;
}
/**
 * 任意の `unknown` 値 (例: Firestore doc snapshot.data()) を安全に
 * `ModeProfile` に変換 (`docId` 引数で外挿 id を上書き可能)。
 *
 * - 入力が non-object / 必須 field (mode / name / profileVersion) 欠落 → null
 * - userFields / postFields は空配列に正規化される (不正値は drop)
 * - その他 optional field は欠落時に undefined
 */
export function parseModeProfile(raw, docId) {
    if (!isRecord(raw))
        return null;
    const mode = pickMode(raw.mode);
    if (!mode)
        return null;
    const name = pickString(raw.name);
    if (!name)
        return null;
    const profileVersion = pickString(raw.profileVersion);
    if (!profileVersion)
        return null;
    const idFromRaw = pickString(raw.id);
    const id = docId ?? idFromRaw;
    if (!id)
        return null;
    const out = {
        id,
        mode,
        name,
        profileVersion,
        userFields: parseFieldDefs(raw.userFields),
        postFields: parseFieldDefs(raw.postFields),
    };
    const modeLabel = pickString(raw.modeLabel);
    if (modeLabel)
        out.modeLabel = modeLabel;
    const description = pickString(raw.description);
    if (description)
        out.description = description;
    const userProfileOptions = parseUserProfileOptions(raw.userProfileOptions);
    if (userProfileOptions)
        out.userProfileOptions = userProfileOptions;
    const recommendedMasters = parseRecommendedMasters(raw.recommendedMasters);
    if (recommendedMasters)
        out.recommendedMasters = recommendedMasters;
    if (raw.createdAt !== undefined)
        out.createdAt = raw.createdAt;
    if (raw.updatedAt !== undefined)
        out.updatedAt = raw.updatedAt;
    if (typeof raw.createdBy === 'string' || raw.createdBy === null) {
        out.createdBy = raw.createdBy;
    }
    if (typeof raw.updatedBy === 'string' || raw.updatedBy === null) {
        out.updatedBy = raw.updatedBy;
    }
    if (raw.archivedAt !== undefined)
        out.archivedAt = raw.archivedAt;
    if (typeof raw.schemaVersion === 'number' && Number.isFinite(raw.schemaVersion)) {
        out.schemaVersion = raw.schemaVersion;
    }
    return out;
}
/** Firestore collection 名 (admin / functions / seed 全てで参照する正)。 */
export const MODE_PROFILES_COLLECTION = 'modeProfiles';
//# sourceMappingURL=modeProfile.js.map